import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Returning from Google OAuth: let AuthCallback exchange the session first.
    if (window.location.hash?.includes("session_id=")) {
      setReady(true);
      return;
    }
    (async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setUser(false);
        setReady(true);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user);
      } catch {
        localStorage.removeItem("token");
        setUser(false);
      }
      setReady(true);
    })();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", data.access_token);
    setUser(data.user);
  };

  const register = async (email, password, name, invite_code) => {
    const { data } = await api.post("/auth/register", { email, password, name, invite_code });
    localStorage.setItem("token", data.access_token);
    setUser(data.user);
  };

  const googleCallback = async (session_id) => {
    const invite_code = localStorage.getItem("pending_invite") || undefined;
    const { data } = await api.post("/auth/google", { session_id, invite_code });
    localStorage.setItem("token", data.access_token);
    localStorage.removeItem("pending_invite");
    setUser(data.user);
    return data.user;
  };

  const startGoogleLogin = (invite_code) => {
    if (invite_code) localStorage.setItem("pending_invite", invite_code);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("token");
    setUser(false);
  };

  const changePassword = async (current_password, new_password) => {
    await api.post("/auth/change-password", { current_password, new_password });
    setUser((u) => (u ? { ...u, must_change_password: false } : u));
  };

  const setInitialPassword = async (new_password) => {
    await api.post("/auth/set-initial-password", { new_password });
    setUser((u) => (u ? { ...u, must_change_password: false } : u));
  };

  const keepCurrentPassword = async () => {
    await api.post("/auth/keep-password");
    setUser((u) => (u ? { ...u, must_change_password: false } : u));
  };

  const isOwner = user && user.role === "owner";
  const canInvite = user && (user.role === "owner" || user.role === "admin");

  return (
    <AuthContext.Provider
      value={{
        user,
        ready,
        login,
        register,
        logout,
        googleCallback,
        startGoogleLogin,
        changePassword,
        setInitialPassword,
        keepCurrentPassword,
        isOwner,
        canInvite,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
